import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, formatEUR, formatNum, formatApiError } from "@/lib/api";
import {
    Sparkle, ArrowRight, Warning, CheckCircle, Info, Lightbulb, Target, Lock
} from "@phosphor-icons/react";

const RATIO_LABEL = {
    liquidez: "Liquidez",
    liquidez_corriente: "Liquidez",
    prueba_acida: "Prueba ácida",
    solvencia: "Solvencia",
    endeudamiento: "Endeudamiento",
    autonomia: "Autonomía",
    autonomia_financiera: "Autonomía",
    roe: "ROE",
    roa: "ROA",
    margen_neto: "Margen neto",
};

const COLORS = ["#0B192C", "#C5A880", "#4A5568", "#A8866A"];

export default function DemoReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get("/public/demo-report");
                setData(d);
            } catch (e) {
                setError(formatApiError(e, "No se pudo cargar la demo"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="elegant-loader"><span /><span /><span /></span>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-20 text-center">
                    <h1 className="serif text-3xl mb-4">No se pudo cargar la demo</h1>
                    <p className="text-[var(--gf-text-secondary)] mb-6">{error}</p>
                    <Link to="/" className="btn-primary">Volver al inicio</Link>
                </main>
                <Footer />
            </div>
        );
    }

    const ratios = data.ratios.ratios;
    const health = data.ratios.health;
    const interp = data.interpretation;

    const chartData = Object.entries(ratios).map(([k, v]) => ({
        name: RATIO_LABEL[k] || k,
        valor: typeof v === "number" ? Number(v.toFixed(2)) : 0,
    }));

    const pieData = [
        { name: "Activos corrientes", value: data.balance.activos_corrientes },
        { name: "Activos no corrientes", value: data.balance.activos_no_corrientes },
        { name: "Pasivos totales", value: data.balance.pasivos_corrientes + data.balance.pasivos_no_corrientes },
        { name: "Patrimonio neto", value: data.balance.patrimonio_neto },
    ];

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            {/* Banner demo */}
            <div className="bg-[var(--gf-gold)] text-white" data-testid="demo-banner">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Sparkle size={20} weight="fill" />
                        <span className="text-sm">
                            <strong>Informe de demostración</strong> — Ejemplo con datos ficticios para mostrarte qué obtendrás
                        </span>
                    </div>
                    <Link to="/registro" className="text-sm bg-white text-[var(--gf-navy)] px-4 py-2 hover:bg-[var(--gf-cream)] transition-colors" data-testid="demo-cta-register">
                        Crear cuenta gratis →
                    </Link>
                </div>
            </div>

            <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    {/* Header */}
                    <div className="mb-12 flex flex-wrap items-end justify-between gap-4 border-b border-[var(--gf-border)] pb-8">
                        <div>
                            <div className="micro-label mb-3">— Informe financiero · Demostración</div>
                            <h1 className="serif text-4xl md:text-5xl">{data.company_name}</h1>
                            <div className="text-sm text-[var(--gf-text-secondary)] mt-2 mono">Periodo: {data.period}</div>
                        </div>
                        <div className="text-right">
                            <div className="micro-label mb-1">Puntuación global</div>
                            <div className="editorial-num text-[var(--gf-gold)]">{interp.puntuacion_global}/100</div>
                        </div>
                    </div>

                    {/* Resumen ejecutivo */}
                    <div className="gf-card mb-10 border-l-4 border-l-[var(--gf-gold)]" data-testid="demo-summary">
                        <div className="micro-label mb-3">Resumen ejecutivo</div>
                        <p className="serif text-lg md:text-xl leading-relaxed">{interp.resumen_ejecutivo}</p>
                    </div>

                    {/* Ratios + gráficos */}
                    <div className="grid lg:grid-cols-2 gap-8 mb-12">
                        <div className="gf-card">
                            <div className="micro-label mb-4">Ratios financieros</div>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={chartData} layout="vertical" margin={{ left: 30 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                                    <XAxis type="number" stroke="#4A5568" fontSize={11} />
                                    <YAxis type="category" dataKey="name" stroke="#4A5568" fontSize={11} width={100} />
                                    <Tooltip />
                                    <Bar dataKey="valor" fill="#C5A880" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="gf-card">
                            <div className="micro-label mb-4">Estructura patrimonial</div>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${(e.percent * 100).toFixed(0)}%`}>
                                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Tabla ratios */}
                    <div className="gf-card mb-12">
                        <div className="micro-label mb-4">Detalle de ratios</div>
                        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                            {Object.entries(ratios).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between py-2 border-b border-[var(--gf-border)]">
                                    <span className="text-sm">{RATIO_LABEL[k] || k}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="mono text-sm">{typeof v === "number" ? formatNum(v) : "—"}</span>
                                        <HealthBadge level={health?.[k]} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Interpretación */}
                    <h2 className="serif text-3xl mb-6">Interpretación experta</h2>
                    <div className="grid md:grid-cols-2 gap-6 mb-12">
                        {interp.interpretacion.map((b) => (
                            <div key={b.titulo} className="gf-card">
                                <div className="flex items-center gap-2 mb-3">
                                    <Info size={20} weight="fill" className="text-[var(--gf-navy)]" />
                                    <h3 className="serif text-xl">{b.titulo}</h3>
                                </div>
                                <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{b.contenido}</p>
                            </div>
                        ))}
                    </div>

                    {/* Consejos */}
                    <h2 className="serif text-3xl mb-6 flex items-center gap-3">
                        <Lightbulb size={28} weight="fill" className="text-[var(--gf-gold)]" />
                        Consejos estratégicos
                    </h2>
                    <div className="space-y-4 mb-12">
                        {interp.consejos.map((c) => (
                            <div key={c.titulo} className="gf-card flex items-start gap-4">
                                <div className={`px-2 py-1 text-[10px] tracking-widest uppercase ${c.prioridad === "alta" ? "bg-[var(--gf-danger)] text-white" : c.prioridad === "media" ? "bg-[var(--gf-gold)] text-white" : "bg-[var(--gf-border)]"}`}>
                                    {c.prioridad}
                                </div>
                                <div className="flex-1">
                                    <h3 className="serif text-lg mb-2">{c.titulo}</h3>
                                    <p className="text-sm text-[var(--gf-text-secondary)]">{c.accion}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Alertas fiscales */}
                    <h2 className="serif text-3xl mb-6 flex items-center gap-3">
                        <Warning size={28} weight="fill" className="text-[var(--gf-danger)]" />
                        Alertas fiscales
                    </h2>
                    <div className="gf-card border-l-4 border-l-[var(--gf-danger)] mb-12">
                        <ul className="space-y-3">
                            {interp.alertas_fiscales.map((a, i) => (
                                <li key={i} className="text-sm leading-relaxed pl-4 border-l-2 border-[var(--gf-danger)]/30">{a}</li>
                            ))}
                        </ul>
                    </div>

                    {/* CTA final */}
                    <div className="bg-[var(--gf-navy)] text-white p-10 md:p-14 text-center" data-testid="demo-final-cta">
                        <Target size={48} weight="light" className="mx-auto mb-4 text-[var(--gf-gold)]" />
                        <h2 className="serif text-3xl md:text-4xl mb-4">
                            ¿Quieres un informe así <span className="italic text-[var(--gf-gold)]">con tus datos</span>?
                        </h2>
                        <p className="text-white/75 mb-8 max-w-2xl mx-auto">
                            Sube tu balance en PDF o Excel y genera un informe profesional en menos de 1 minuto.
                            Empieza gratis con 3 balances básicos.
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link to="/registro" className="btn-gold" data-testid="demo-cta-final-register">
                                Crear cuenta gratis <ArrowRight size={18} />
                            </Link>
                            <Link to="/pagos" className="btn-outline" style={{ borderColor: "white", color: "white" }}>
                                Ver planes
                            </Link>
                        </div>
                    </div>

                    <div className="mt-8 text-xs text-center text-[var(--gf-text-muted)] flex items-center justify-center gap-2">
                        <Lock size={14} />
                        Este informe usa datos ficticios. Tus datos reales se procesan cifrados y nunca se comparten.
                    </div>
                </motion.div>
            </main>

            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function HealthBadge({ level }) {
    if (!level) return null;
    const map = {
        bueno: { c: "bg-green-50 text-green-700 border-green-200", l: "Bueno", I: CheckCircle },
        alerta: { c: "bg-amber-50 text-amber-700 border-amber-200", l: "Atención", I: Warning },
        riesgo: { c: "bg-red-50 text-red-700 border-red-200", l: "Riesgo", I: Warning },
        "n/a": { c: "bg-gray-50 text-gray-600 border-gray-200", l: "—", I: Info },
    };
    const m = map[level] || map["n/a"];
    const I = m.I;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border ${m.c}`}>
            <I size={10} weight="fill" /> {m.l}
        </span>
    );
}
